import React, { useState } from "react";
import useSWR from "swr";
import { ethers } from "ethers";
import { PLACEHOLDER_ACCOUNT } from "lib/legacy";

import { getContract } from "config/contracts";

import Token from "sdk/abis/Token.json";
import RewardReader from "sdk/abis/RewardReader.json";

import Checkbox from "components/Checkbox/Checkbox";

import "./ClaimEsMoney.css";

import arbitrumIcon from "img/ic_arbitrum_96.svg";
import bscIcon from "img/ic_bsc_96.svg";

import { Trans, t } from "@lingui/macro";
import { ARBITRUM, BSC } from "config/chains";
import { callContract, contractFetcher } from "lib/contracts";
import { bigNumberify, formatAmount, formatAmountFree, parseValue } from "lib/numbers";
import { useChainId } from "lib/chains";
import ExternalLink from "components/ExternalLink/ExternalLink";
import Button from "components/Button/Button";
import useWallet from "lib/wallets/useWallet";
import { usePendingTxns } from "lib/usePendingTxns";
import { bigMath } from "lib/bigmath";

const VEST_WITH_MONEY_ARB = "VEST_WITH_MONEY_ARB";
const VEST_WITH_MLP_ARB = "VEST_WITH_MLP_ARB";
const VEST_WITH_MONEY_RON = "VEST_WITH_MONEY_RON";
const VEST_WITH_MLP_RON = "VEST_WITH_MLP_RON";

export function getVestingDataV2(vestingInfo) {
  if (!vestingInfo || vestingInfo.length === 0) {
    return;
  }

  const keys = ["moneyVester", "mlpVester"];
  const data = {};
  const propsLength = 12;

  for (let i = 0; i < keys.length; i++) {
    const key = keys[i];
    data[key] = {
      pairAmount: vestingInfo[i * propsLength],
      vestedAmount: vestingInfo[i * propsLength + 1],
      escrowedBalance: vestingInfo[i * propsLength + 2],
      claimedAmounts: vestingInfo[i * propsLength + 3],
      claimable: vestingInfo[i * propsLength + 4],
      maxVestableAmount: vestingInfo[i * propsLength + 5],
      combinedAverageStakedAmount: vestingInfo[i * propsLength + 6],
      cumulativeReward: vestingInfo[i * propsLength + 7],
      transferredCumulativeReward: vestingInfo[i * propsLength + 8],
      bonusReward: vestingInfo[i * propsLength + 9],
      averageStakedAmount: vestingInfo[i * propsLength + 10],
      transferredAverageStakedAmount: vestingInfo[i * propsLength + 11],
    };

    data[key + "PairAmount"] = data[key].pairAmount;
    data[key + "VestedAmount"] = data[key].vestedAmount;
    data[key + "EscrowedBalance"] = data[key].escrowedBalance;
    data[key + "ClaimSum"] = data[key].claimedAmounts + data[key].claimable;
    data[key + "Claimable"] = data[key].claimable;
    data[key + "MaxVestableAmount"] = data[key].maxVestableAmount;
    data[key + "CombinedAverageStakedAmount"] = data[key].combinedAverageStakedAmount;
    data[key + "CumulativeReward"] = data[key].cumulativeReward;
    data[key + "TransferredCumulativeReward"] = data[key].transferredCumulativeReward;
    data[key + "BonusReward"] = data[key].bonusReward;
    data[key + "AverageStakedAmount"] = data[key].averageStakedAmount;
    data[key + "TransferredAverageStakedAmount"] = data[key].transferredAverageStakedAmount;
  }

  return data;
}

function getVestingValues({ minRatio, amount, vestingDataItem }) {
  if (!vestingDataItem || !amount) {
    return;
  }

  let currentRatio = 0n;

  const ratioMultiplier = 10000;
  const maxVestableAmount = vestingDataItem.maxVestableAmount;
  const nextMaxVestableEsMoney = maxVestableAmount + amount;

  const combinedAverageStakedAmount = vestingDataItem.combinedAverageStakedAmount;
  if (maxVestableAmount > 0) {
    currentRatio = bigMath.mulDiv(combinedAverageStakedAmount, ratioMultiplier, maxVestableAmount);
  }

  const transferredCumulativeReward = vestingDataItem.transferredCumulativeReward;
  const nextTransferredCumulativeReward = transferredCumulativeReward + amount;
  const cumulativeReward = vestingDataItem.cumulativeReward;
  const totalCumulativeReward = cumulativeReward + nextTransferredCumulativeReward;

  let nextCombinedAverageStakedAmount = combinedAverageStakedAmount;

  if (combinedAverageStakedAmount < totalCumulativeReward * minRatio) {
    const averageStakedAmount = vestingDataItem.averageStakedAmount;
    let nextTransferredAverageStakedAmount = totalCumulativeReward * minRatio;
    nextTransferredAverageStakedAmount =
      nextTransferredAverageStakedAmount - bigMath.mulDiv(averageStakedAmount, cumulativeReward, totalCumulativeReward);
    nextTransferredAverageStakedAmount = bigMath.mulDiv(
      nextTransferredAverageStakedAmount,
      totalCumulativeReward,
      nextTransferredCumulativeReward
    );

    nextCombinedAverageStakedAmount =
      bigMath.mulDiv(averageStakedAmount, cumulativeReward, totalCumulativeReward) +
      bigMath.mulDiv(nextTransferredAverageStakedAmount, nextTransferredCumulativeReward, totalCumulativeReward);
  }

  const nextRatio = bigMath.mulDiv(nextCombinedAverageStakedAmount, ratioMultiplier, nextMaxVestableEsMoney);

  const initialStakingAmount = currentRatio * maxVestableAmount;
  const nextStakingAmount = nextRatio * nextMaxVestableEsMoney;

  return {
    maxVestableAmount,
    currentRatio,
    nextMaxVestableEsMoney,
    nextRatio,
    initialStakingAmount,
    nextStakingAmount,
  };
}

export default function ClaimEsMoney() {
  const { active, account, signer } = useWallet();
  const { chainId } = useChainId();
  const [selectedOption, setSelectedOption] = useState("");
  const [isClaiming, setIsClaiming] = useState(false);
  const [value, setValue] = useState("");
  const [, setPendingTxns] = usePendingTxns();

  const isArbitrum = chainId === ARBITRUM;

  const esMoneyIouAddress = getContract(chainId, "ES_MONEY_IOU");

  const { data: esMoneyIouBalance } = useSWR(
    isArbitrum && [
      `ClaimEsMoney:esMoneyIouBalance:${active}`,
      chainId,
      esMoneyIouAddress,
      "balanceOf",
      account || PLACEHOLDER_ACCOUNT,
    ],
    {
      fetcher: contractFetcher(signer, Token),
    }
  );

  const arbRewardReaderAddress = getContract(ARBITRUM, "RewardReader");
  const ronRewardReaderAddress = getContract(BSC, "RewardReader");

  const arbVesterAdddresses = [getContract(ARBITRUM, "MoneyVester"), getContract(ARBITRUM, "MlpVester")];
  const ronVesterAdddresses = [getContract(BSC, "MoneyVester"), getContract(BSC, "MlpVester")];

  const { data: arbVestingInfo } = useSWR(
    [
      `StakeV2:vestingInfo:${active}`,
      ARBITRUM,
      arbRewardReaderAddress,
      "getVestingInfoV2",
      account || PLACEHOLDER_ACCOUNT,
    ],
    {
      fetcher: contractFetcher(undefined, RewardReader, [arbVesterAdddresses]),
    }
  );

  const { data: ronVestingInfo } = useSWR(
    [
      `StakeV2:vestingInfo:${active}`,
      BSC,
      ronRewardReaderAddress,
      "getVestingInfoV2",
      account || PLACEHOLDER_ACCOUNT,
    ],
    {
      fetcher: contractFetcher(undefined, RewardReader, [ronVesterAdddresses]),
    }
  );

  const arbVestingData = getVestingDataV2(arbVestingInfo);
  const ronVestingData = getVestingDataV2(ronVestingInfo);

  let amount = parseValue(value, 18);

  let maxVestableAmount;
  let currentRatio;

  let nextMaxVestableEsMoney;
  let nextRatio;

  let initialStakingAmount;
  let nextStakingAmount;

  let stakingToken = "staked MONEY";

  const shouldShowStakingAmounts = false;

  if (selectedOption === VEST_WITH_MONEY_ARB && arbVestingData) {
    const result = getVestingValues({
      minRatio: bigNumberify(4),
      amount,
      vestingDataItem: arbVestingData.moneyVester,
    });

    if (result) {
      ({ maxVestableAmount, currentRatio, nextMaxVestableEsMoney, nextRatio, initialStakingAmount, nextStakingAmount } =
        result);
    }
  }

  if (selectedOption === VEST_WITH_MLP_ARB && arbVestingData) {
    const result = getVestingValues({
      minRatio: bigNumberify(320),
      amount,
      vestingDataItem: arbVestingData.mlpVester,
    });

    if (result) {
      ({ maxVestableAmount, currentRatio, nextMaxVestableEsMoney, nextRatio, initialStakingAmount, nextStakingAmount } =
        result);
    }

    stakingToken = "MLP";
  }

  if (selectedOption === VEST_WITH_MONEY_RON && ronVestingData) {
    const result = getVestingValues({
      minRatio: bigNumberify(4),
      amount,
      vestingDataItem: ronVestingData.moneyVester,
    });

    if (result) {
      ({ maxVestableAmount, currentRatio, nextMaxVestableEsMoney, nextRatio, initialStakingAmount, nextStakingAmount } =
        result);
    }
  }

  if (selectedOption === VEST_WITH_MLP_RON && ronVestingData) {
    const result = getVestingValues({
      minRatio: bigNumberify(320),
      amount,
      vestingDataItem: ronVestingData.mlpVester,
    });

    if (result) {
      ({ maxVestableAmount, currentRatio, nextMaxVestableEsMoney, nextRatio, initialStakingAmount, nextStakingAmount } =
        result);
    }

    stakingToken = "MLP";
  }

  const getError = () => {
    if (!active) {
      return t`Wallet not connected`;
    }

    if (esMoneyIouBalance && esMoneyIouBalance == 0n) {
      return t`No esMONEY to claim`;
    }

    if (amount === undefined) {
      return t`Enter an amount`;
    }

    if (selectedOption === "") {
      return t`Select an option`;
    }

    return false;
  };

  const error = getError();

  const getPrimaryText = () => {
    if (error) {
      return error;
    }

    if (isClaiming) {
      return t`Claiming...`;
    }

    return t`Claim`;
  };

  const isPrimaryEnabled = () => {
    return !error && !isClaiming;
  };

  const claim = () => {
    setIsClaiming(true);

    let receiver;

    if (selectedOption === VEST_WITH_MONEY_ARB) {
      receiver = "0x544a6ec142Aa9A7F75235fE111F61eF2EbdC250a";
    }

    if (selectedOption === VEST_WITH_MLP_ARB) {
      receiver = "0x9d8f6f6eE45275A5Ca3C6f6269c5622b1F9ED515";
    }

    if (selectedOption === VEST_WITH_MONEY_RON) {
      receiver = "0x171a321A78dAE0CDC0Ba3409194df955DEEcA746";
    }

    if (selectedOption === VEST_WITH_MLP_RON) {
      receiver = "0x28863Dd19fb52DF38A9f2C6dfed40eeB996e3818";
    }

    const contract = new ethers.Contract(esMoneyIouAddress, Token.abi, signer);
    callContract(chainId, contract, "transfer", [receiver, amount], {
      sentMsg: t`Claim submitted!`,
      failMsg: t`Claim failed.`,
      successMsg: t`Claim completed!`,
      setPendingTxns,
    }).finally(() => {
      setIsClaiming(false);
    });
  };

  return (
    <div className="ClaimEsMoney Page page-layout">
      <div className="default-container">
        <div className="Page-title">
          <Trans>Claim esMONEY</Trans>
        </div>
        {!isArbitrum && (
          <div className="Page-description">
            <br />
            <Trans>Please switch your network to Ronin.</Trans>
          </div>
        )}
        {isArbitrum && (
          <div>
            <div className="Page-description hyphens-auto">
              <br />
              <Trans>You have {formatAmount(esMoneyIouBalance, 18, 2, true)} esMONEY (IOU) tokens.</Trans>
              <br />
              <br />
              <Trans>The address of the esMONEY (IOU) token is {esMoneyIouAddress}.</Trans>
              <br />
              <Trans>
                The esMONEY (IOU) token is transferrable. You can add the token to your wallet and send it to another
                address to claim if you'd like.
              </Trans>
              <br />
              <br />
              <Trans>Select your vesting option below then click "Claim".</Trans>
              <br />
              <Trans>
                After claiming, the esMONEY tokens will be airdropped to your account on the selected network within 7
                days.
              </Trans>
              <br />
              <Trans>The esMONEY tokens can be staked or vested at any time.</Trans>
              <br />
              <Trans>
                Your esMONEY (IOU) balance will decrease by your claim amount after claiming, this is expected behaviour.
              </Trans>
              <br />
              <Trans>
                You can check your claim history{" "}
                <ExternalLink href={`https://arbiscan.io/token/${esMoneyIouAddress}?a=${account}`}>here</ExternalLink>.
              </Trans>
            </div>
            <br />
            <div className="ClaimEsMoney-vesting-options">
              <Checkbox
                className="arbitrum vest-option"
                isChecked={selectedOption === VEST_WITH_MONEY_ARB}
                setIsChecked={() => setSelectedOption(VEST_WITH_MONEY_ARB)}
              >
                <Trans>Vest with MONEY on Ronin</Trans>
                <img src={arbitrumIcon} alt="Ronin" />
              </Checkbox>
              <Checkbox
                className="arbitrum vest-option"
                isChecked={selectedOption === VEST_WITH_MLP_ARB}
                setIsChecked={() => setSelectedOption(VEST_WITH_MLP_ARB)}
              >
                <Trans>Vest with MLP on Ronin</Trans>
                <img src={arbitrumIcon} alt="Ronin" />
              </Checkbox>
              <Checkbox
                className="bsc vest-option"
                isChecked={selectedOption === VEST_WITH_MONEY_RON}
                setIsChecked={() => setSelectedOption(VEST_WITH_MONEY_RON)}
              >
                <Trans>Vest with MONEY on Bsc</Trans>
                <img src={bscIcon} alt="Bsc" />
              </Checkbox>
              <Checkbox
                className="bsc vest-option"
                isChecked={selectedOption === VEST_WITH_MLP_RON}
                setIsChecked={() => setSelectedOption(VEST_WITH_MLP_RON)}
              >
                <Trans>Vest with MLP on Bsc</Trans>
                <img src={bscIcon} alt="Bsc" />
              </Checkbox>
            </div>
            <br />
            {!error && (
              <div className="muted">
                <Trans>
                  You can currently vest a maximum of {formatAmount(maxVestableAmount, 18, 2, true)} esMONEY tokens at a
                  ratio of {formatAmount(currentRatio, 4, 2, true)} {stakingToken} to 1 esMONEY.
                </Trans>
                {shouldShowStakingAmounts && `${formatAmount(initialStakingAmount, 18, 2, true)}.`}
                <br />
                <Trans>
                  After claiming you will be able to vest a maximum of {formatAmount(nextMaxVestableEsMoney, 18, 2, true)}{" "}
                  esMONEY at a ratio of {formatAmount(nextRatio, 4, 2, true)} {stakingToken} to 1 esMONEY.
                </Trans>
                {shouldShowStakingAmounts && `${formatAmount(nextStakingAmount, 18, 2, true)}.`}
                <br />
                <br />
              </div>
            )}
            <div>
              <div className="ClaimEsMoney-input-label muted">
                <Trans>Amount to claim</Trans>
              </div>
              <div className="ClaimEsMoney-input-container">
                <input type="number" placeholder="0.0" value={value} onChange={(e) => setValue(e.target.value)} />
                {value !== formatAmountFree(esMoneyIouBalance, 18, 18) && (
                  <div
                    className="ClaimEsMoney-max-button"
                    onClick={() => setValue(formatAmountFree(esMoneyIouBalance, 18, 18))}
                  >
                    <Trans>MAX</Trans>
                  </div>
                )}
              </div>
            </div>
            <br />
            <div>
              <Button
                variant="primary-action"
                className="w-full"
                disabled={!isPrimaryEnabled()}
                onClick={() => claim()}
              >
                {getPrimaryText()}
              </Button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
